// R212 — **HER PAUSED MEMBERSHIP READ "FREE" IN THE ONE ACCOUNT CONTROL ON EVERY PAGE OF THIS SITE,
// AND THE SERVER HAD ALREADY SENT THE ANSWER.**
//
// `handleSubscriptionStatus` composes `frozen` and `frozenTier` onto `/api/membership/status`, under
// a comment that says in as many words: *"the members menu says what happened rather than showing a
// paying member 'Free' with no explanation."* **It does not.** `SubStatus` declared neither field,
// `AccountSubscription` rendered neither, and `AccountMenuPanel` named neither — so what a frozen
// year-long Vet actually read under *Subscription* was
//
//     Free
//
// because `tierLabel` is `tierByKey(ent.tier)?.label || "Free"` and a frozen member's `ent.tier` is
// `free`. **That is R87's own headline sentence** — *the account menu called a paying member "free"*
// — arriving through the SUCCESSFUL read path this time, which is the one half R87 could not close:
// it repaired the read that did not LAND, and this is a read that landed and was not looked at.
//
// **AND IT IS L19's FINDING ONE PAYLOAD OVER.** L19 measured that `frozen` was false on every
// entitlements payload this site has ever sent, so *"L15's whole member-facing half had never
// rendered"*. This is the OTHER member-facing surface L15's server comment claims, and it had never
// rendered either — for the opposite reason: the field arrives correctly and **nobody reads it.**
// R38/R165's trap on the money path — a capability the house documents and does not have — and
// R121/R122's class, documentation read as installation.
//
// **WHY NO CENSUS COULD SEE IT, AND THAT IS STRUCTURAL RATHER THAN AN OVERSIGHT.** R203 hunts a
// field the server sends that no client reads, and its reader is deliberately the WIDE one — *a key
// is read if its name appears as a word anywhere in the client* — because the narrow reader errs in
// the forbidden direction (it reports a live field as dead, and the repair a session takes for that
// reading is a DELETION). `frozen` and `frozenTier` appear as words in `tier-catalog.ts`,
// `tier-state.ts`, `TierCards.tsx` and `PurpleBox.tsx` — **because the LOCK payload's copies of them
// are read.** A per-NAME census over one client cannot tell one payload's field from another's, and
// R203 says so about its own direction. Nothing was wrong with the census.
//
// **THREE STATES, NEVER TWO (R18), AND THE BLOCK SHOWS EXACTLY ONE.** A gift, a pause, or an
// ordinary membership. They are answered here rather than by three ternaries in the render, so two
// of them can never paint at once — a comped member reading *your membership is paused* over a room
// that is wide open to her is L15's own grant-outranks-freeze ruling broken on the screen.
// **The server already enforces that ruling** (`frozenNow = frozen && access.tier === "free"`), so
// this is not a second answer key (R28/R29): it is the render's own law, agreeing with the server by
// construction and unable to disagree with it.
//
// **PRESENCE, NEVER TRUTHINESS (R53/R136/R137).** This arrives off JSON, where `Boolean("false")` is
// `true` and `Number(true)` is 1 — the coercion that once cast a whole chart at the equator. Only a
// real `true` is frozen, and only a non-empty STRING is a label.
//
// **THE LABEL IS THE SERVER'S OWN COMPOSITION AND IS NEVER BUILT HERE.** `AccountSubscription`'s own
// header states that rule about `tierLabel` and `priceText` (ITEM 14b: re-translating a
// server-composed sentence on the client is a second answer key for copy the ledger already
// recorded), so `frozenTierLabel` rides beside `frozenTier` off `tierByKey`, exactly as `tierLabel`
// does. **A held tier the house cannot NAME is still a paused membership** — `heldTier` answers
// `String(row.tier || "free")` and `tierByKey("free")` is `null`, so an empty label is reachable,
// and the honest answer is the paused sentence WITHOUT a tier name rather than a name invented for
// it (R141).
//
// This module imports NOTHING, which is the entry requirement that lets a `.mjs` gate load and RUN
// it: `scripts/r212-paused-membership.mjs` drives it over every payload this route and its edge can
// produce, including several this repo does not contain (R129/R135).

export type MembershipLineStatus = {
  tier?: string;
  gift?: unknown | null;
  frozen?: boolean;
  frozenTier?: string | null;
  frozenTierLabel?: string | null;
};

export type MembershipLineState = "gift" | "paused" | "plain";

export type MembershipLine = {
  state: MembershipLineState;
  // The tier she is one tap from reopening, in the SERVER's words. Empty on every other state, and
  // empty on a pause the house cannot name — never a key like `vet` leaking onto her screen
  // (S1-A/R117: a member-facing line may never say the machinery).
  heldLabel: string;
};

const heldLabelOf = (status: MembershipLineStatus | null | undefined): string => {
  const label = status?.frozenTierLabel;
  return typeof label === "string" && label.trim() ? label.trim() : "";
};

export function membershipLine(status: MembershipLineStatus | null | undefined): MembershipLine {
  // The gift outranks the pause, and it outranks it HERE as well as on the server, because this is
  // the one place both facts are in the same hand. L15's ruling, on the screen.
  if (status?.gift) return { state: "gift", heldLabel: "" };
  if (status?.frozen === true) return { state: "paused", heldLabel: heldLabelOf(status) };
  return { state: "plain", heldLabel: "" };
}

// **WHAT THE BLOCK'S OWN `data-account-state` SAYS, DERIVED FROM THE LAW RATHER THAN RE-DECIDED IN
// THE MARKUP.** Two rendered instruments already read that attribute to find the gift state
// (`d2-grants`, `item13-grant-journey`), so it is the identity a walk has (R41) — and deriving it
// means a state this law knows about can never be one the attribute does not.
export function accountStateAttr(line: MembershipLine, tier?: string | null): string {
  if (line.state === "plain") return String(tier || "free");
  return line.state;
}
